import { CdkDragDrop, CdkDragMove, CdkDragStart } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnChanges,
  OnInit,
  QueryList,
  Signal,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'fast-deep-equal';
import { produce } from 'immer';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import { makeStylesForTag, TagConfig } from '@blaze-runtime/engine-domain';
import {
  ActionsDisplayMode,
  CanvasElementPropertiesWithData,
  ContainerElementMetaUiFeatures,
  DataBindingSourceInfo,
  DEFAULT_ELEMENT_PADDING,
  getBackground,
  getColumnFormatOptions,
  getFormatForField,
  ITEMS_CONTAINER_ACTION_ADD,
  ITEMS_CONTAINER_ACTION_REORDER,
  ITEMS_CONTAINER_ACTION_VIEW,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  PropertiesConfigInputColorPaletteModel,
  PropertiesConfigInputContainerItemActionModel,
  PropertiesConfigInputListCustomFieldModel,
  RuntimeContext,
  TableColumnFormat,
} from '@blaze-shared/elements-domain';
import { sortByKey } from '@blaze-shared/platform-util';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  convertBlazeSchemaToElementSchema,
  DatasetColumn,
  DatasetRow,
  findColumn,
  getDisplayStringFromLinkValue,
  isDatasetLinkValue,
  sortColumnArrayValues,
} from '@blaze-shared/schema-domain';
import {
  AnyFormatValueOptions,
  DEFAULT_SECURE_TEXT_DISPLAY_VALUE,
  DEFAULT_SECURE_TEXT_VALUE,
  formatGeoPoint,
  isGeoPoint,
} from '@blaze-shared/shared-feature';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import {
  countedShareReplay,
  isDeepEqual,
  isNullishOrEmptyString,
  resizeRxUtils,
} from '@blaze-shared/shared-util';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { ListLikeActionResult } from '../../services/container-actions.service';
import { CanvasElementBaseWithActionsComponent } from '../__base/base-with-actions.component';
import { ElementDataActions, OnContainerActionParams } from '../__base/element-data-actions';

import {
  BoardCacheEntry,
  BoardData,
  KanbanBoardPropsModel,
  RuntimeCustomField,
} from './kanban-board.models';

@UntilDestroy()
@Component({
  selector: 'be-kanban-board',
  templateUrl: './kanban-board.component.html',
  styleUrls: ['./kanban-board.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [ElementDataActions<'kanbanBoard'>],
  host: {
    '[style.--b-kbn-font-size]': 'fontSize',
    '[style.--b-kbn-font-family]': 'fontFamily',
    '[style.--b-kbn-line-height]': 'lineHeight',
    '[style.--b-kbn-column-width]': 'columnWidth',
    '[style.--b-kbn-internal-padding]': 'internalPadding',
    '[style.--b-kbn-cd-title-color]': 'cdTitleColor',
    '[style.--b-kbn-cd-desc-color]': 'cdDescColor',
    '[style.--b-kbn-cd-actions-color]': 'cdActionsColor',
    '[style.--b-kbn-cd-fill-color]': 'cdFillColor',
    '[style.--b-kbn-cd-outline-color]': 'cdOutlineColor',
    '[style.--b-kbn-cd-radius]': 'cdRadius',
    '[style.--b-kbn-border-box-height]': 'borderBoxHeight',
    '[style.--b-kbn-b-fill-color]': 'bFillColor',
    '[style.--b-kbn-b-outline-color]': 'bOutlineColor',
    '[style.--b-kbn-b-outline-width]': 'bOutlineWidth',
    '[style.--b-kbn-b-radius]': 'bRadius',
  },
})
export class KanbanBoardComponent
  extends CanvasElementBaseWithActionsComponent<'kanbanBoard'>
  implements CanvasElementComponent<'kanbanBoard'>, OnChanges, OnInit, AfterViewInit
{
  private readonly datasetRowsService = inject(DatasetRowsService);

  protected readonly elementDataActions = inject(ElementDataActions);

  @ViewChildren('columnsList') columnsList!: QueryList<ElementRef<HTMLUListElement>>;
  @ViewChild('boardContainer') boardContainer?: ElementRef;

  private readonly dataSource$$ = new BehaviorSubject<DataBindingSourceInfo | undefined>(undefined);
  private readonly allActions$$ = new BehaviorSubject<
    PropertiesConfigInputContainerItemActionModel[]
  >([]);
  private readonly customFields$$ = new BehaviorSubject<
    PropertiesConfigInputListCustomFieldModel[]
  >([]);
  private readonly boardData$$ = new BehaviorSubject<BoardData>([]);
  private readonly hasBoundValue$$ = new BehaviorSubject<boolean>(!!this.bindingResults?.['value']);

  readonly elementActionsKeyRoot = 'b-kac';
  readonly columnNameHeightPx = 40;

  fontSize = FALLBACK_THEME.value.fontSize;
  fontFamily = FALLBACK_THEME.value.fontFamily;
  lineHeight = FALLBACK_THEME.value.lineHeight;
  columnWidth = '240px';
  internalPadding = DEFAULT_ELEMENT_PADDING;

  // Card
  cdTitleColor = FALLBACK_THEME.value.title;
  cdDescColor = FALLBACK_THEME.value.value;
  cdActionsColor = FALLBACK_THEME.value.value;
  cdFillColor = FALLBACK_THEME.value.fill;
  cdOutlineColor = FALLBACK_THEME.value.outline;
  cdRadius = FALLBACK_THEME.value.radius;
  borderBoxHeight = '0px';

  // Background
  bFillColor = FALLBACK_THEME.value.fill;
  bOutlineColor = FALLBACK_THEME.value.outline;
  bOutlineWidth = '1px';
  bRadius = FALLBACK_THEME.value.radius;

  value$: Observable<DatasetRow[]>;
  value: any[];
  columnTitleField$: Observable<string | undefined>;
  cardTitleField = '';
  cardTitleFieldFormat: TableColumnFormat = 'text';
  cardTitleFieldFormatOptions: AnyFormatValueOptions | undefined;
  cardDescriptionField = '';
  cardDescriptionFieldFormat: TableColumnFormat = 'text';
  cardDescriptionFieldFormatOptions: AnyFormatValueOptions | undefined;
  cardIdField = '';
  actionsList: PropertiesConfigInputContainerItemActionModel[] = [];
  hasBoundValue$: Observable<boolean>;
  boardData$ = this.boardData$$.asObservable();
  elementActionsKey!: string;

  columnIds$: Observable<string[]>;
  selectedHeaderPalette$: Observable<PropertiesConfigInputColorPaletteModel | undefined>;
  columnNameStyles: Record<string, string | undefined>[] = [];
  tagConfig: TagConfig = [];

  labelEnabled = false;
  labelText = '';
  labelIcon = '';
  actionsDisplayMode!: ActionsDisplayMode;
  itemAdd: PropertiesConfigInputContainerItemActionModel = {
    id: ITEMS_CONTAINER_ACTION_ADD,
    label: '',
  };
  actionsGrouped = false;
  itemViewEnabled: Signal<boolean | undefined>;
  itemAddEnabled: Signal<boolean | undefined>;
  itemDragDropEnabled: Signal<boolean | undefined>;

  wrapperStyles: Record<string, string> | null = null;
  actionsContainerStyles!: Record<string, string>;

  runtimeCustomFields$: Observable<RuntimeCustomField[]>;
  visibleRowCustomFields$: Observable<RuntimeCustomField[][]>;

  get shouldShowCardsList(): boolean {
    return !!(
      this.cardTitleField ||
      this.cardDescriptionField ||
      this.customFields$$.value.length ||
      this.actionsList.length
    );
  }

  constructor() {
    super();

    this.hasBoundValue$ = this.hasBoundValue$$.pipe(distinctUntilChanged());

    this.elementDataActions.props = toSignal(this.props$);
    this.elementDataActions.dataValue = this.dataValue;
    this.elementDataActions.elementId = toSignal(this.elementId$);
    this.elementDataActions.elementType = this.elementType;

    this.value$ = this.getDataInput('value').dataInput$.pipe(
      map((payload) => {
        return (payload?.data || []) as DatasetRow[];
      }),
    );

    this.runtimeCustomFields$ = this.customFields$$.pipe(
      map((fields) =>
        fields.map((field) => ({
          ...field,
          columnName: this.getColumnName(field.column),
        })),
      ),
    );

    this.columnTitleField$ = this.props$.pipe(
      map((props) => props?.content.fields?.columnTitle),
      distinctUntilChanged(),
    );

    this.selectedHeaderPalette$ = this.props$.pipe(
      map((props) => props?.style.header?.palette),
      distinctUntilChanged(isEqual),
    );

    const columnTitleData$ = combineLatest({
      columnTitleField: this.columnTitleField$,
      dataSource: this.dataSource$$.pipe(startWith(undefined)),
    }).pipe(
      distinctUntilChanged((a, b) => {
        return a.columnTitleField === b.columnTitleField && isEqual(a.dataSource, b.dataSource);
      }),
      map(({ columnTitleField, dataSource }) => {
        const dataSourceColumn = findColumn(dataSource?.columns, columnTitleField);
        return { field: columnTitleField, column: dataSourceColumn };
      }),
      filter(({ field, column }, index) => index === 0 || !!(field && column)),
    );

    // Create board values
    combineLatest({
      columnTitleData: columnTitleData$,
      hasBoundValue: this.hasBoundValue$,
      rows: this.value$,
      linkValues: columnTitleData$.pipe(
        switchMap(({ column }) => this.fetchLinkValuesForColumn(column)),
      ),
    })
      .pipe(
        map(({ columnTitleData: { field, column }, hasBoundValue, rows, linkValues }) => {
          // Handle default value
          if (!hasBoundValue) {
            return ['Backlog', 'In Progress', 'Done'].map((id, index) => ({
              id,
              displayName: id,
              cards: [{ index, value: { _cardTitle: 'Title', _cardDesc: 'Description' } }],
            })) satisfies BoardData;
          }

          if (!field || !column || !Array.isArray(rows)) {
            return [];
          }

          const columnTitleFieldName = this.getColumnName(field);
          if (!columnTitleFieldName) {
            console.error(`Could not match column name ${field}`);
            return [];
          }

          // Use Set to have only unique values.
          // JS Sets preserve insertion order and this property is used here.
          const columnIds = new Set<string>();
          const tempBoard: Record<string, BoardCacheEntry> = {};
          const isTagDataType = column?.dataType === 'Tag';
          const isLinkDataType = column?.dataType === 'Link';

          rows.forEach((row, index) => {
            const rawValue = row[columnTitleFieldName];
            let id: string | undefined | null;
            if (Array.isArray(rawValue)) {
              id = sortColumnArrayValues(
                rawValue.map((v) => (isDatasetLinkValue(v) ? v.rowId : v)),
                column,
              ).join(',');
            } else if (isDatasetLinkValue(rawValue)) {
              id = rawValue.rowId;
            } else if (isGeoPoint(rawValue)) {
              id = formatGeoPoint(rawValue);
            } else {
              id = rawValue;
            }
            id = !isNullishOrEmptyString(id) ? String(id) : '';

            columnIds.add(id);
            if (tempBoard[id]) {
              tempBoard[id].rows.push({ index, value: row });
              tempBoard[id].raw = rawValue;
            } else {
              tempBoard[id] = { raw: rawValue, rows: [{ index, value: row }] };
            }
          });

          // For tags get column names from definition to preserve column definition order.
          if (isTagDataType && column.tagDefinition) {
            const originalColumnIds = [...columnIds];
            columnIds.clear();
            column.tagDefinition.items.forEach((def) => {
              columnIds.add(def.value);
              originalColumnIds
                .filter((colId) => colId.startsWith(def.value + ','))
                .forEach((colId) => {
                  columnIds.add(colId);
                });
            });
          }

          // For links get the all possible values from linked dataset.
          if (isLinkDataType && linkValues) {
            const originalColumnIds = [...columnIds];
            columnIds.clear();
            Object.entries(linkValues).forEach(([id]) => {
              columnIds.add(id);
              originalColumnIds
                .filter((colId) => colId.startsWith(id + ','))
                .forEach((colId) => {
                  columnIds.add(colId);
                });
            });
          }

          let boardData: BoardData = [];
          columnIds.forEach((id) => {
            const columnValue = tempBoard[id];
            if (!columnValue) {
              let displayName: string;
              const isSecureValue = id === DEFAULT_SECURE_TEXT_VALUE;
              if (isSecureValue) {
                displayName = DEFAULT_SECURE_TEXT_DISPLAY_VALUE;
              } else if (isLinkDataType) {
                displayName = linkValues?.[id] ?? id;
              } else {
                displayName = id;
              }

              boardData.push({ id, displayName, cards: [] });
            } else {
              let displayName: string;
              if (Array.isArray(columnValue.raw)) {
                displayName = sortColumnArrayValues(
                  columnValue.raw.map((v) =>
                    isDatasetLinkValue(v) ? getDisplayStringFromLinkValue(v.display) : v,
                  ),
                  column,
                ).join(', ');
              } else if (isDatasetLinkValue(columnValue.raw)) {
                displayName = getDisplayStringFromLinkValue(columnValue.raw.display);
              } else {
                displayName = id;
              }
              if (displayName === DEFAULT_SECURE_TEXT_VALUE) {
                displayName = DEFAULT_SECURE_TEXT_DISPLAY_VALUE;
              }
              boardData.push({ id, displayName, cards: columnValue.rows });
            }
          });

          if (!isTagDataType && !isLinkDataType) {
            boardData = sortByKey(boardData, 'displayName');
          }

          return boardData;
        }),
        untilDestroyed(this),
      )
      .subscribe((boardData) => {
        this.boardData$$.next(boardData);
      });

    this.columnIds$ = this.boardData$$.pipe(map((boardData) => boardData.map((c) => c.id)));

    // Create styles for column headers
    combineLatest({
      paletteModel: this.selectedHeaderPalette$,
      columnIds: this.columnIds$,
      schema: this.createPaletteSchemaObservable(this.dataSource$$),
    })
      .pipe(
        map(({ paletteModel, columnIds, schema }) => {
          const palette = this.elementTagsService.mapValuesToColorPalette(
            columnIds,
            this.props?.content.fields?.columnTitle,
            schema,
            paletteModel,
          );
          return columnIds.map((name) => makeStylesForTag(name, palette));
        }),
        untilDestroyed(this),
      )
      .subscribe((colors) => {
        this.columnNameStyles = colors;
      });

    this.createTagConfig(
      this.props$.pipe(map((props) => props?.content.customFields)),
      this.props$.pipe(map((props) => props?.content.columns)),
      this.createPaletteSchemaObservable(this.dataSource$$),
      this.dataValue.data$,
    ).subscribe((tagConfig) => {
      this.tagConfig = tagConfig;
      this.cdr.detectChanges();
    });

    this.elementDataActions.visibleRowActions$ = this.elementDataActions.getVisibleActions(
      this.runtimeContext$,
      this.value$,
      this.props$.pipe(
        map((props) =>
          props?.content.actions?.items.filter((action) => action.label || action.icon),
        ),
        distinctUntilChanged(isDeepEqual),
      ),
      this.element$,
    );

    this.visibleRowCustomFields$ = combineLatest({
      context: this.runtimeContext$,
      rows: this.value$,
      customFields: this.runtimeCustomFields$.pipe(
        map((fields) => fields.filter((field) => field.columnName)),
      ),
    }).pipe(
      map(({ context, rows, customFields }) => {
        if (!customFields || !rows?.length) {
          return [];
        }

        // If no custom fields are conditionally visible - show the same custom fields for all rows
        if (!customFields.some((field) => !!field.visibilityExpression)) {
          return rows.map(() => customFields);
        }

        // Normalization is done here for improved performance - the normalized context is shared across all rows
        const normalizedContext = this.normalizeLinkedParams(context);

        return rows.map((rowData) => {
          // Add normalized row data to the context
          const rowContext = {
            ...normalizedContext,
            output: this.normalizeLinkedParams(rowData as RuntimeContext),
          };

          // Show card custom field if the visibility expression is not provided or if it resolves to true
          return customFields.filter(
            (field) =>
              !field.visibilityExpression ||
              !!this.evaluateExpression(field.visibilityExpression, rowContext, true),
          );
        });
      }),
      countedShareReplay(),
    );

    this.itemViewEnabled = toSignal(
      this.visibilityComponentScope((props) => ({
        enabled: props.content.itemView?.enabled,
        expression: props.content.itemView?.visibilityExpression,
      })),
    );
    this.itemAddEnabled = toSignal(
      this.visibilityComponentScope((props) => ({
        enabled: props.content.itemAdd.enabled,
        expression: props.content.itemAdd.visibilityExpression,
      })),
    );
    this.itemDragDropEnabled = toSignal(
      this.props$.pipe(
        filter(Boolean),
        switchMap((props) => {
          if (!dragDropPossible(props, this.schemaService)) {
            return of(false);
          }
          return this.visibilityComponentScope((props) => ({
            enabled: props.content.itemDragDrop.enabled,
            expression: props.content.itemDragDrop.expression,
          }));
        }),
      ),
    );

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.hasBoundValue$$.next(!!this.bindingResults?.['value']);

    if (changes?.['elementId'] && !this.elementActionsKey) {
      this.elementActionsKey = `${this.elementActionsKeyRoot}-${this.elementId}`;
    }

    if (changes['props']) {
      const { content } = this.props || {};

      this.itemAdd.label = content?.itemAdd?.label || '';
      this.itemAdd.styleSheetId = content?.itemAdd?.styleSheetId;
      this.itemAdd.styleSheetTarget = content?.itemAdd?.styleSheetTarget;

      const dataSource = (this.props as CanvasElementPropertiesWithData)?.data?.find(
        (d) => d.key === 'value',
      )?.dataSource;
      this.dataSource$$.next(dataSource);

      if (this.hasBoundValue$$.value) {
        this.cardTitleField = this.getColumnName(content?.fields?.cardTitle) || '';
        this.cardTitleFieldFormat = getFormatForField(
          this.props?.data,
          'value',
          this.cardTitleField,
          'text',
        );
        this.cardTitleFieldFormatOptions = getColumnFormatOptions(
          this.props?.data,
          'value',
          this.cardTitleField,
        );
        this.cardDescriptionField = this.getColumnName(content?.fields?.cardDescription) || '';
        this.cardDescriptionFieldFormatOptions = getColumnFormatOptions(
          this.props?.data,
          'value',
          this.cardDescriptionField,
        );
        this.cardDescriptionFieldFormat = getFormatForField(
          this.props?.data,
          'value',
          this.cardDescriptionField,
          'text',
        );
        this.cardIdField = this.getIdColumnName(dataSource) ?? '';
      } else {
        this.cardTitleField = '_cardTitle';
        this.cardDescriptionField = '_cardDesc';
        this.cardIdField = '_id;';
      }

      this.actionsList =
        content?.actions?.items?.filter((action) => action.label || action.icon) || [];
      this.allActions$$.next([this.itemAdd, ...this.actionsList]);

      this.updateStyles();
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<ContainerElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = features.title ?? '';
        const updatedFields = (this.props?.content?.customFields || []).map((field) => {
          const colProps = findColumn(features.columns, field.column);
          const result = produce(field, (draft) => {
            draft.format = this.getFieldFormat(field.column);
            if (colProps) {
              draft.label = colProps.name || draft.label;
              draft.displayFormat = colProps.format || draft.displayFormat;
              draft.precision = colProps.precision || draft.precision;
              draft.currency = colProps.currency ?? draft.currency;
              draft.duration = colProps.duration ?? draft.duration;
              draft.dateFormat = colProps.dateFormat ?? draft.dateFormat;
              draft.dateTimeFormat = colProps.dateTimeFormat ?? draft.dateTimeFormat;
            }
          });
          return result;
        });

        this.customFields$$.next(updatedFields);
        safeDetectChanges(this.cdr);
      });

    /**
     * Declare output schemas when no value is bound to element.
     */
    if (!this.bindingResults?.['value']) {
      this.setDefaultEventSchema('action');
    }

    /**
     * Declare output schemas when bindings change.
     */
    this.schema$.pipe(untilDestroyed(this)).subscribe((schema: BlazeSchema) => {
      this.setDefaultEventSchema(
        'action',
        convertBlazeSchemaToElementSchema(schema, this.elementId, 'action'),
      );
    });

    this.initActionStyles(this.elementActionsKey, this.allActions$$.asObservable(), [
      'efh-action-btn',
      'action-item',
    ]);
  }

  ngAfterViewInit(): void {
    this.initColumnBorderRender();
  }

  onAction({
    event,
    action,
    dataRow,
  }: OnContainerActionParams): Promise<ListLikeActionResult | undefined> | undefined {
    event?.stopPropagation();
    if (dataRow === null) {
      return;
    }

    const dataSource = (this.props as CanvasElementPropertiesWithData)?.data?.find(
      (data) => data?.key === 'value',
    )?.dataSource;
    if (!dataSource) {
      console.error(`Action "${action.label}" failed. Data source not configured.`);
      return;
    }

    const idColumn = this.schemaService.getDataSourceIdColumn(dataSource.id, dataSource.sourceType);
    const dataRowId = dataRow && idColumn ? dataRow[idColumn] : undefined;

    return this.containerActionsService.handleListlikeAction({
      dataSource,
      action,
      props: this.props as KanbanBoardPropsModel,
      output: this.output,
      dataRow,
      dataRowSchema: this.getEventSchema('action'),
      dataRowId,
    });
  }

  onAddNewRow(event: Event): void {
    if (!this.itemAddEnabled) {
      return;
    }

    this.onAction({ event, action: this.itemAdd, dataRow: {} });
  }

  onViewRow(event: Event, dataRow: DatasetRow): void {
    if (!this.itemViewEnabled()) {
      return;
    }

    this.onAction({ event, action: { id: ITEMS_CONTAINER_ACTION_VIEW, label: 'View' }, dataRow });
  }

  getFieldFormat(columnIdOrName?: string): TableColumnFormat {
    const column = findColumn(this.props?.content.columns, columnIdOrName);
    return column?.format || 'text';
  }

  columTypeClicked(columnType: string, value?: string): void {
    switch (columnType) {
      case 'url':
        return this.dataTypeActions.openPage(value);
      case 'email':
        return this.dataTypeActions.copy(value);
      default:
        return;
    }
  }

  onDrop(event: CdkDragDrop<string[]>): void {
    if (event.container.data === event.previousContainer.data) {
      return;
    }

    // move the card for immediate effect
    const boardData = this.boardData$$.value;
    const draggedCard = boardData.find(
      (column) => column.displayName === event.previousContainer.data[0],
    )?.cards[event.previousIndex];
    if (draggedCard) {
      const tempBoardData = boardData.map((column) =>
        column.displayName === event.previousContainer.data[0]
          ? { ...column, cards: column.cards.filter((card) => card !== draggedCard) }
          : column.displayName === event.container.data[0]
            ? { ...column, cards: [draggedCard, ...column.cards] }
            : column,
      );
      this.boardData$$.next(tempBoardData);
    }

    // trigger proper update and reload
    const rowId = event.item.data as string;
    const status = event.container.data[0];
    const dataSource = this.dataSource$$.value;
    const datasetId = dataSource?.id && this.schemaService.getDatasetId(dataSource?.id);
    if (!datasetId) {
      throw new Error('unable to retrieve dataset id');
    }
    const columnTitleFieldId = this.props?.content.fields?.columnTitle;
    if (!columnTitleFieldId) {
      throw new Error('column title field id missing');
    }
    const columnTitleFieldName = this.props?.content.columns.find(
      ({ id }) => id === columnTitleFieldId,
    )?.name;
    if (!columnTitleFieldName) {
      throw new Error('column title field name missing');
    }
    this.datasetRowsService
      .update(datasetId, { value: { [columnTitleFieldId]: status }, rowId })
      .subscribe((dataRow) => {
        this.containerActionsService.handleListlikeAction({
          dataSource,
          action: {
            id: ITEMS_CONTAINER_ACTION_REORDER,
            action: { workflowId: this.props?.content.itemDragDrop.workflowId },
          },
          props: this.props as KanbanBoardPropsModel,
          output: this.output,
          dataRow,
          dataRowSchema: this.getEventSchema('action'),
          dataRowId: rowId,
        });
      });
  }

  dragMoved(event: CdkDragMove | CdkDragStart): void {
    // disable canvas drag&drop
    event.event.stopPropagation();
  }

  initColumnBorderRender(): void {
    const boardContainerResize$ = resizeRxUtils
      .fromResizeObserver(this.boardContainer?.nativeElement)
      .pipe(
        map((entries) => entries[entries.length - 1]),
        filter(Boolean),
      );

    const columnsListElement$ = this.columnsList?.changes.pipe(
      map((v) => v.first?.nativeElement as HTMLUListElement | undefined),
      filter(Boolean),
    );

    combineLatest({
      boardContainerResize: boardContainerResize$,
      columnsListElement: columnsListElement$,
      visibleRowCustomFields: this.visibleRowCustomFields$,
      visibleRowActions: this.elementDataActions.visibleRowActions$,
      boardData: this.boardData$$,
    })
      .pipe(
        // Wait for the DOM to be ready after changes
        debounceTime(0),
        untilDestroyed(this),
        map(({ boardContainerResize, columnsListElement }) => {
          const columnsListHeight = columnsListElement.getBoundingClientRect().height ?? 0;

          return Math.max(
            0,
            Math.min(boardContainerResize.contentRect.height, columnsListHeight) -
              this.columnNameHeightPx,
          );
        }),
      )
      .subscribe((height) => {
        this.borderBoxHeight = `${height}px`;
        this.cdr.markForCheck();
      });
  }

  private updateStyles(): void {
    const { content, style } = this.props || {};
    const { title, board, actions } = content || {};
    const { columnWidth } = board || {};
    const { general, fontSize, fontFamily, lineHeight, card, background, container } = style || {};
    const { heading } = general || {};

    if (!heading || !columnWidth || !card || !background || !container) {
      return;
    }

    const labelEnabled = title?.enabled ?? true;
    const actionsContainerStyles: Record<string, string> = {};

    if (actions?.align === 'left') {
      actionsContainerStyles['justify-content'] = 'flex-start';
    }

    this.labelEnabled = labelEnabled;
    this.labelIcon = title?.icon || '';
    this.actionsDisplayMode = actions?.mode ?? 'always';
    this.actionsGrouped = actions?.mode === 'group';

    this.fontSize = fontSize as string;
    this.fontFamily = fontFamily as string;
    this.lineHeight = lineHeight as string;
    this.columnWidth = columnWidth ?? '240px';

    this.internalPadding = general?.offset.padding ?? DEFAULT_ELEMENT_PADDING;

    this.cdTitleColor = card.title.color1;
    this.cdDescColor = card.description.color1;
    this.cdActionsColor = card.actions.color1;
    this.cdFillColor = getBackground(card.fill);
    this.cdOutlineColor =
      card.outline.selectorType !== 'none' ? card.outline.color1 : 'transparent';
    this.cdRadius = card.radius;

    this.bFillColor = getBackground(background.fill);
    this.bOutlineColor = background.outline.color1;
    this.bOutlineWidth =
      background.outline.selectorType !== 'none' ? background.outlineWidth : '0px';
    this.bRadius = background.radius;

    this.wrapperStyles = {
      ...makeContainerVisualEffectStyles(background.visualEffect),
      ...makeBorderStyles(container.outline, container.outlineWidth),
    };
    this.actionsContainerStyles = actionsContainerStyles;
  }

  private fetchLinkValuesForColumn(
    column: DatasetColumn | undefined,
  ): Observable<Record<string, any> | undefined> {
    if (column?.dataType !== 'Link' || !column.link?.displayColumn) {
      return of(undefined);
    }

    const idColumn = this.schemaService.getIdColumnName(column.link.tableId);
    const displayColumn = column.link.displayColumn;
    return this.datasetRowsService.get({ datasetId: column.link.tableId, maxResults: 100 }).pipe(
      map((rows) =>
        rows?.reduce<Record<string, any>>((acc, row) => {
          acc[row[idColumn]] = row[displayColumn];
          return acc;
        }, {}),
      ),
    );
  }
}

export function dragDropPossible(
  model: KanbanBoardPropsModel,
  schemaService: SchemaService,
): boolean {
  const dataSource = (model as CanvasElementPropertiesWithData).data?.find(
    (data) => data?.key === 'value',
  )?.dataSource;
  const datasetId = dataSource?.id && schemaService.getDatasetId(dataSource?.id);
  const schema = datasetId ? schemaService.getSchema(datasetId) : undefined;
  return schema?.columns.some(({ id }) => id === model.content.fields.columnTitle) ?? false;
}
